import {
	ClientEntityType,
	JobDocumentEntityType,
	OrganizationEntityType,
} from "@/lib/supabase/supabaseTypes";
import { CalculateTotalsResult } from "../../lib/utils/calculate";
import { PdfTemplateConfiguration } from "../../context/PdfTemplateConfigurationContext";
import { PdfTemplateType } from "../document-template.types";

// Utility function to get effective formatting value (general or document-specific override)
const getEffectiveFormattingValue = (
	generalValue: boolean | undefined,
	documentConfig: any,
	overrideProperty: string
): boolean => {
	// If document has override enabled, disable the feature for this document
	if (documentConfig?.[overrideProperty]) {
		return false;
	}
	// Otherwise use general setting
	return generalValue ?? false;
};

const formatPercentage = (value: number): string => {
	if (Math.abs(value - Math.round(value)) < 0.001) {
		return Math.round(value).toString();
	}
	return value.toFixed(1);
};

const discountsRowLabel = (
	totals: CalculateTotalsResult,
	discountFixedAmounts: string
) => {
	const workDiscountPercent = totals.amountWorkAndFixedRate.value > 0
		? (totals.deductionsWorkAndFixedRate.value / totals.amountWorkAndFixedRate.value) * 100
		: 0;

	const materialDiscountPercent = totals.amountMaterials.value > 0
		? (totals.deductionsMaterials.value / totals.amountMaterials.value) * 100
		: 0;

	const discountWorkLabel = workDiscountPercent > 0
		? "Arbeit -" + formatPercentage(workDiscountPercent) + "%"
		: "";
	const discountMaterialLabel = materialDiscountPercent > 0
		? "Mat. -" + formatPercentage(materialDiscountPercent) + "%"
		: "";
	const discountFixedAmountsLabel = discountFixedAmounts && discountFixedAmounts !== "0.00"
		? "Fixe Abzüge " + discountFixedAmounts
		: "";
	return `Abzüge (${Array.from([discountWorkLabel, discountMaterialLabel, discountFixedAmountsLabel].filter((e) => e)).join(", ")})`;
};

export const PdfTableTotalsSection: React.FC<{
	totals: CalculateTotalsResult;
	organization: OrganizationEntityType;
	numberOfColumns: number;
	numberOfAdditionalColumns: number; // additional columns to the right of the total values
	jobDocument?: JobDocumentEntityType; // optional, if totals are for one document (which might have discounts)
	monthlyDiscountOnWorkAndFixedRate?: string; // optional, if totals are for a monthly invoice (which might have a monthly discount)
	monthlyDiscountOnSubtotal?: string; // optional, if totals are for a monthly invoice (which might have a monthly discount)
	client?: ClientEntityType; //optional, if totals are for a monthly invoice (which might have discounts)
	pdfTemplateConfiguration: PdfTemplateConfiguration;
	isMonthlyInvoice?: boolean;
	isMwstEust: boolean;
	templateType?: PdfTemplateType | string;
	currency?: string | null; // optional currency from job -> client -> region
}> = ({
	totals,
	organization,
	numberOfColumns,
	numberOfAdditionalColumns,
	jobDocument,
	monthlyDiscountOnWorkAndFixedRate,
	monthlyDiscountOnSubtotal,
	client,
	pdfTemplateConfiguration,
	isMonthlyInvoice,
	isMwstEust,
	templateType,
	currency,
}) => {
	const {
		amountMaterials,
		amountPostage,
		amountExternalWork,
		amountDiscounts,
		amountWorkAndFixedRate,
		deductionsMaterials,
		deductionsWorkAndFixedRate,
		deductions,
		subTotal,
		mwst,
		total,
		taxpunkte,
		mwstPercentage,
	} = totals;

	const generalConfig = pdfTemplateConfiguration?.general;
	const documentConfig = templateType && pdfTemplateConfiguration?.[templateType as keyof PdfTemplateConfiguration];

	const getRowHeight = () => {
		const density = generalConfig?.tableDensity ?? "md";
		return {
			sm: "3mm",
			md: "5mm", 
			lg: "8mm"
		}[density];
	};

	const rowHeight = getRowHeight();

	const summaryRows: { label: string; value: string }[] = [];

	if (amountWorkAndFixedRate.value > 0) {
		summaryRows.push({
			label: `Arbeit (${taxpunkte.text}Tp)`,
			value: amountWorkAndFixedRate.text,
		});
	}

	if (amountMaterials.value > 0) {
		summaryRows.push({
			label: "Material",
			value: amountMaterials.text,
		});
	}

	if (
		!isMonthlyInvoice &&
		(deductionsWorkAndFixedRate.value > 0 ||
			deductionsMaterials.value > 0 ||
			amountDiscounts.value > 0)
	) {
		summaryRows.push({
			label: discountsRowLabel(
				totals,
				amountDiscounts.text
			),
			value: "-" + deductions.text,
		});
	}

	if (amountPostage.value > 0) {
		summaryRows.push({
			label: "Porto",
			value: amountPostage.text,
		});
	}

	if (amountExternalWork.value > 0) {
		summaryRows.push({
			label: "Fremdarbeiten",
			value: amountExternalWork.text,
		});
	}

	// Add empty row
	summaryRows.push({
		label: "",
		value: "",
	});

	if (
		isMonthlyInvoice &&
		deductionsWorkAndFixedRate &&
		deductionsWorkAndFixedRate.value > 0 &&
		deductionsWorkAndFixedRate.text !== "0.00"
	) {
		const workDiscountPercent = amountWorkAndFixedRate.value > 0
			? (deductionsWorkAndFixedRate.value / amountWorkAndFixedRate.value) * 100
			: 0;
		summaryRows.push({
			label: `Abzüge auf Arbeit (${formatPercentage(workDiscountPercent)}%)`,
			value: `-${deductionsWorkAndFixedRate.text}`,
		});
	}
	if (
		isMonthlyInvoice &&
		deductionsMaterials &&
		deductionsMaterials.value > 0 &&
		deductionsMaterials.text !== "0.00"
	) {
		const materialDiscountPercent = amountMaterials.value > 0
			? (deductionsMaterials.value / amountMaterials.value) * 100
			: 0;
		summaryRows.push({
			label: `Abzüge auf Material (${formatPercentage(materialDiscountPercent)}%)`,
			value: `-${deductionsMaterials.text}`,
		});
	}
	if (
		isMonthlyInvoice &&
		monthlyDiscountOnWorkAndFixedRate &&
		monthlyDiscountOnWorkAndFixedRate !== "0.00"
	) {
		summaryRows.push({
			label: `Monatsrabatt auf Arbeit (${client?.monthly_discount}%)`,
			value: "-" + monthlyDiscountOnWorkAndFixedRate,
		});
	}

	if (
		isMonthlyInvoice &&
		monthlyDiscountOnSubtotal &&
		monthlyDiscountOnSubtotal !== "0.00"
	) {
		summaryRows.push({
			label: `Monatsrabatt (${client?.monthly_discount}%)`,
			value: "-" + monthlyDiscountOnSubtotal,
		});
	}

	summaryRows.push({
		label: "Subtotal",
		value: subTotal.text,
	});

	// The Mwst. (/EUSt.) could be zero if public.organizations.is_mwst_included is true (meaning the organization has selected that the tariffs and articles already include MwSt./EUSt.)
	// Show VAT even when zero if configuration is set
	const showZeroVat = generalConfig?.showZeroVat ?? false;
	if (mwst.value > 0 || showZeroVat) {
		summaryRows.push({
			label: `${isMwstEust ? "EUSt." : "MwSt."} (${
				mwstPercentage
			}%)`,
			value: mwst.text,
		});
	}

	// Add akonto reallocation if present
	if (totals.akontoReallocation && totals.akontoReallocation.value > 0) {
		summaryRows.push({
			label: "Akonto",
			value: "-" + totals.akontoReallocation.text,
		});

		// Add empty row after akonto
		summaryRows.push({
			label: "",
			value: "",
		});
	}

	// Add rounding difference if present and non-zero
	if (totals.roundingDifference && Math.abs(totals.roundingDifference.value) > 0.001) {
		summaryRows.push({
			label: isMonthlyInvoice ? "Rundungsdifferenz (Summe)" : "Rundungsdifferenz",
			value: totals.roundingDifference.value >= 0
				? totals.roundingDifference.text
				: totals.roundingDifference.text,
		});
	}

	// Add empty row
	summaryRows.push({
		label: "",
		value: "",
	});

	// Get effective currency display value (considering document-specific override)
	const showTotalCurrency = getEffectiveFormattingValue(
		generalConfig?.showTotalCurrency ?? false,
		documentConfig,
		'overrideShowTotalCurrency'
	);
	// Use currency from job -> client -> region hierarchy
	const effectiveCurrency = currency || "CHF";
	const totalLabel = showTotalCurrency ? `Total ${effectiveCurrency}` : "Total";

	summaryRows.push({
		label: totalLabel,
		value: total.text,
	});

	return (
		<>
			{summaryRows.map((row, i) => {
				// Add empty rows
				if (row.label === "" && row.value === "") {
					return (
						<tr
							key={i}
							style={{
								height: rowHeight,
								backgroundColor: generalConfig?.tableBackgroundColor || "#d3d3d3",
							}}
						>
							<td
								colSpan={
									numberOfColumns + numberOfAdditionalColumns
								}
								style={{
									height: rowHeight,
								}}
							></td>
						</tr>
					);
				}
				const isTotal = row.label === "Total" || row.label.startsWith("Total ");
				const isTotalBold = generalConfig?.isTotalBold ?? false;
				return (
					<SummaryRow
						key={row.label}
						label={row.label}
						value={row.value}
						numberOfColumns={numberOfColumns}
						numberOfAdditionalColumns={numberOfAdditionalColumns}
						// Add borders to the first and last summary rows
						style={
							i === 0
								? { borderTop: "1px solid black", ...(isTotal && isTotalBold ? { fontWeight: "bold" } : {}) }
								: i === summaryRows.length - 1
									? { borderBottom: "1px solid black", ...(isTotal && isTotalBold ? { fontWeight: "bold" } : {}) }
									: (isTotal && isTotalBold ? { fontWeight: "bold" } : {})
						}
						generalConfig={generalConfig}
						rowHeight={rowHeight}
					/>
				);
			})}
		</>
	);
};

const SummaryRow: React.FC<{
	label: string;
	value: string;
	numberOfColumns: number;
	numberOfAdditionalColumns: number;
	style?: React.CSSProperties;
	generalConfig: PdfTemplateConfiguration["general"];
	rowHeight: string;
}> = ({ label, value, style, numberOfColumns, numberOfAdditionalColumns, generalConfig, rowHeight }) => (
	<tr
		style={{
			...style,
			height: rowHeight,
			backgroundColor: generalConfig?.tableBackgroundColor || "#d3d3d3",
		}}
	>
		<td colSpan={2} />
		<td align="left" colSpan={numberOfColumns - 3}>
			{label}
		</td>
		<td align="right" colSpan={1}>
			{value}
		</td>
		{numberOfAdditionalColumns > 0 && (
			<td colSpan={numberOfAdditionalColumns} />
		)}
	</tr>
);
